import { createSelector } from 'reselect';
import { AssetType } from '@ynomia/core/dist/blueprint';
import { AssetFilter, AssetSelection } from '../context';
import map from './map';
import { getAssetTypes } from '.';
import { PAGINATION_DEFAULT_CONFIG } from '../config/constants';

export const getAssetManagerSelectedAssetTypeId = createSelector([
  map.assetManagerId,
  getAssetTypes,
  map.assetManagerKeyedById,
], (assetManagerId, assetTypes, assetManagerKeyedById) => {
  if (!assetManagerKeyedById || !assetManagerKeyedById[assetManagerId]?.selectedAssetTypeId) {
    return assetTypes[0].id;
  }

  return assetManagerKeyedById[assetManagerId].selectedAssetTypeId;
});

export const getSelectedAssetType = createSelector(
  [getAssetManagerSelectedAssetTypeId, getAssetTypes],
  (selectedAssetTypeId, assetTypes) => (
    assetTypes.find(({ type }) => type === selectedAssetTypeId) || assetTypes[0]) as AssetType,
);

const getCurrentAssetManagerAssetType = createSelector([
  map.assetManagerId,
  map.assetManagerKeyedById,
  getAssetManagerSelectedAssetTypeId,
], (assetManagerId, assetManagerKeyedById, selectedAssetTypeId) => (
  assetManagerKeyedById?.[assetManagerId]?.assetManagerAssetTypes?.[selectedAssetTypeId]
));

export const getAssetManagerFilters = createSelector([
  map.assetManagerKeyedById,
  getCurrentAssetManagerAssetType,
], (assetManagerKeyedById, assetManagerAssetTypes): AssetFilter => {
  if (!assetManagerKeyedById || !assetManagerAssetTypes || !assetManagerAssetTypes.filters) {
    return {
      searchTags: [],
      searchText: '',
      dateFilter: [],
      selectedLayerIds: [],
      assetStatusFilter: {
        toggle: null,
        ids: [],
      },
      observationTypeFilter: {},
      multiSearchModeFilter: 'or',
      openedLayerId: null,
      tableFilter: {},
      pagination: PAGINATION_DEFAULT_CONFIG,
      fieldFilters: {},
      tableSorter: null,
      timeTravelDate: null,
      selectedBandIds: {},
    };
  }
  return assetManagerAssetTypes.filters;
});

export const getAssetManagerCollapsable = createSelector([
  getCurrentAssetManagerAssetType,
], assetManagerAssetType => (
  assetManagerAssetType?.collapsable
));

export const getAssetManagerShowPlanView = createSelector([
  getCurrentAssetManagerAssetType,
], assetManagerAssetTypes => (
  assetManagerAssetTypes?.showPlanView
));

export const getAssetManagerSelectedPlanId = createSelector([
  getCurrentAssetManagerAssetType,
  getSelectedAssetType,
], (
  assetManagerAssetTypes,
  selectedAssetType,
) => {
  const plans = selectedAssetType?.plans || [];
  return assetManagerAssetTypes?.selectedPlanId ?? plans[0]?.id ?? undefined;
});

export const getAssetManagerAssetSelection = createSelector([
  map.assetManagerKeyedById,
  getCurrentAssetManagerAssetType,
], (assetManagerKeyedById, assetManagerAssetTypes): AssetSelection => {
  const defaultParams = {
    showSelectedOnly: false,
    lastManualSelectionUpdate: undefined,
    selectedAssets: [],
    hoveredAssetId: undefined,
    hoverOrigin: undefined,
  };
  const { assetSelection } = assetManagerAssetTypes || {};
  if (!assetManagerKeyedById || !assetSelection) {
    return defaultParams;
  }

  return {
    ...defaultParams,
    ...assetSelection,
  };
});

export const getAssetManagerSearch = createSelector([
  map.assetManagerKeyedById,
  getCurrentAssetManagerAssetType,
], (assetManagerKeyedById, assetManagerAssetTypes): Array<string> => {
  const { filters } = assetManagerAssetTypes || {};
  const { searchTags = [], searchText = '' } = filters || {};

  if (!assetManagerKeyedById || !filters) {
    return [];
  }

  if (!searchText && !searchTags.length) {
    return [];
  }

  return [searchText, ...searchTags];
});
